import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { ComboboxOption, ComboboxOptionProps } from './ComboboxOption'
import { MultipleValue, SingleValue } from '../Select/types'

export type UseComboboxProps<AM, T> = {
  allowMultiple: AM
  children: ReactNode
  defaultValue?: T
  value?: T
  onChange?: (value: T) => void
}

export const useCombobox = ({
  defaultValue,
  value,
  onChange,
  children,
  allowMultiple,
}: UseComboboxProps<true, MultipleValue> | UseComboboxProps<false, SingleValue>) => {
  const isControlled = value !== undefined

  const { options, optionIds } = useMemo(() => {
    const optionsArray: React.ReactElement<ComboboxOptionProps>[] = []
    const idsArray: string[] = []
    React.Children.forEach(children, (child) => {
      if (React.isValidElement<ComboboxOptionProps>(child) && child.type === ComboboxOption) {
        optionsArray.push(child)
        idsArray.push(child.props.id)
      }
    })
    return { options: optionsArray, optionIds: idsArray }
  }, [children])

  const [selectedIndexes, setSelectedIndexes] = useState<number[]>(() => {
    if (Array.isArray(defaultValue)) {
      return defaultValue
        .map((value) => optionIds.findIndex((id) => id === value))
        .filter((index) => index !== -1)
    }
    return []
  })

  /// Update selected indexes when value changes
  useEffect(() => {
    if (!isControlled || !Array.isArray(value)) return
    const newIndexes = value.map((val) => optionIds.indexOf(val)).filter((index) => index !== -1)
    setSelectedIndexes(newIndexes)
  }, [value, optionIds, setSelectedIndexes, isControlled])

  const selectedChildren = useMemo(
    () =>
      selectedIndexes
        .map((index) => {
          const optionsProps = options[index]?.props
          if (!optionsProps) return null
          return {
            children: optionsProps.children,
            icon: optionsProps.icon,
            index,
          }
        })
        .filter((item): item is NonNullable<typeof item> => item !== null),
    [selectedIndexes, options],
  )

  const handleSelect = useCallback(
    (index: number) => {
      let newIndexes
      if (allowMultiple) {
        // Existing logic for multiple selection
        newIndexes = [...selectedIndexes]
        const selectedIndex = newIndexes.indexOf(index)

        if (selectedIndex !== -1) {
          newIndexes.splice(selectedIndex, 1)
        } else {
          newIndexes.push(index)
        }
      } else {
        // Logic for single selection
        newIndexes = [index]
      }

      if (!isControlled) {
        setSelectedIndexes(newIndexes)
      }
      if (!onChange) {
        return
      }
      if (allowMultiple) {
        onChange(newIndexes.map((idx) => optionIds[idx]))
      } else {
        onChange(optionIds[index])
      }
    },
    [allowMultiple, isControlled, onChange, optionIds, selectedIndexes],
  )

  return {
    selectedIndexes,
    handleSelect,
    selectedChildren,
  }
}
